// W2.5 A1 — client-side event tracking (docs/ANALYTICS_SCOPE.md).
//
// Design rules, in order of importance:
//   1. NEVER blocks or breaks the app. Every public function is wrapped so a metrics
//      failure can only ever cost us a data point, not a user interaction. No retry
//      queue: analytics is not worth one (offline events are simply dropped).
//   2. NO new identifiers, NO PII. The only identity that leaves the device is the
//      existing functional deviceId (localStorage — it powers device management and
//      exists whether or not analytics does) plus the household code the app already
//      sends on every state call. The worker hashes both with rotating salts before
//      anything reaches the analytics store; raw values are never written to it
//      (worker/handlers/metrics.js). Child names, free text, and exact ages are never
//      sent — children appear only as an age BAND via ageBand() below.
//   3. Fire-and-forget delivery. Events queue in memory and flush in small batches via
//      navigator.sendBeacon (survives tab close); fetch(keepalive) is the fallback.
//
// The event taxonomy lives in docs/ANALYTICS_SCOPE.md §3.1 — add new events there
// AND to the worker's allowlist (worker/handlers/metrics.js EVENT_NAMES), or they're
// dropped server-side. That friction is deliberate: it keeps the dataset queryable.

import { DEVICE_ID_KEY } from "./constants.js";
import { apiUrl } from "./buildFlags.js";

const { useEffect, useRef } = React;

const FLUSH_MS = 5000; // batch window — long enough to coalesce a burst, short enough to survive short visits
const MAX_BATCH = 20; // worker caps AE writes at 25/invocation; stay under it
const MAX_QUEUE = 200; // hard memory ceiling; oldest dropped first beyond this
const SESSION_GAP_MS = 30 * 60 * 1000; // same "session" definition as GA's default
const LS_LAST_ACTIVE = "lb-metrics-last-active"; // sessionStorage: dies with the tab, like a session should
const LS_ACQ = "lb-acquisition"; // sessionStorage: first-touch params, pending household creation
const LS_FIRST_TOUCH_SENT = "lb-first-touch-sent";

let queue = [];
let flushTimer = null;
let householdCode = null;
let currentMode = null; // "kid" | "parent" | null (pre-mode screens)
let currentBand = null; // active kid's age band — stamped onto kid-mode events
let currentScreen = null; // last screen id — what app_exit reports as the exit stage
let started = false;

function now() {
  return Date.now();
}

function safe(fn) {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

// The SAME deviceId device-management already uses (Root.jsx / api.js) — read, never
// created here: if it doesn't exist yet, this returns null and the worker treats the
// events as anonymous rather than minting an identifier just for analytics.
function readDeviceId() {
  return safe(() => localStorage.getItem(DEVICE_ID_KEY)) || null;
}

// LB-154 (Sep 2026): three classes, not two, so the laptop question (LB-153 / option D) gets
// a number. "laptop" = a fine pointer on a screen at least 900px on its short side; "tablet"
// = a coarse pointer with the short side ≥ 600; else phone. Coarse on purpose — never the
// exact dimensions, which would be a fingerprint.
function deviceClass() {
  return safe(() => {
    const short = Math.min(window.screen.width, window.screen.height);
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (fine && short >= 900) return "laptop";
    return short >= 600 ? "tablet" : "phone";
  }) || "phone";
}

// Orientation at flush time — the other half of "how is this device being held".
function orientation() {
  return safe(() => (window.innerWidth > window.innerHeight ? "landscape" : "portrait")) || "portrait";
}

function displayMode() {
  return safe(() => (window.matchMedia("(display-mode: standalone)").matches || navigator.standalone ? "pwa" : "browser")) || "browser";
}

// 5–7 / 8–10 / 11+ — the ONLY form in which a child's age ever reaches analytics.
export function ageBand(child) {
  const age = child && typeof child.age === "number" ? child.age : null;
  if (age === null) return null;
  if (age <= 7) return "5-7";
  if (age <= 10) return "8-10";
  return "11+";
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_MS);
}

function send(events) {
  const body = JSON.stringify({
    code: householdCode,
    deviceId: readDeviceId(),
    device: deviceClass(),
    display: displayMode(),
    orient: orientation(),
    events,
  });
  const ok = safe(() => navigator.sendBeacon && navigator.sendBeacon(apiUrl("/api/metrics"), new Blob([body], { type: "application/json" })));
  if (!ok) {
    // keepalive lets the request outlive an unloading page, mirroring sendBeacon.
    safe(() => fetch(apiUrl("/api/metrics"), { method: "POST", body, keepalive: true }).catch(() => {}));
  }
}

export function flush() {
  if (queue.length === 0) return;
  while (queue.length > 0) {
    send(queue.splice(0, MAX_BATCH));
  }
}

// track("till_logged", { band: "5-7", success: true })   // key must be in PROP_BLOBS
// Props are a FLAT bag of short strings/numbers/booleans; anything else is dropped
// client-side so a stray object can never smuggle PII into a beacon.
export function track(event, props) {
  safe(() => {
    if (typeof event !== "string" || !event) return;
    const p = {};
    if (props && typeof props === "object") {
      for (const k of Object.keys(props)) {
        const v = props[k];
        if (v === null || v === undefined) continue;
        if (typeof v === "string") p[k] = v.slice(0, 64);
        else if (typeof v === "number" && Number.isFinite(v)) p[k] = v;
        else if (typeof v === "boolean") p[k] = v;
      }
    }
    if (currentMode && !p.mode) p.mode = currentMode;
    if (currentBand && currentMode === "kid" && !p.band) p.band = currentBand;
    queue.push({ e: event, p, ts: now() });
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
    safe(() => sessionStorage.setItem(LS_LAST_ACTIVE, String(now())));
    if (queue.length >= MAX_BATCH) flush();
    else scheduleFlush();
  });
}

// Kid/parent mode is a dimension on every event rather than an event of its own —
// App.jsx calls this from an effect whenever the mode changes.
export function setMetricsMode(mode) {
  currentMode = mode === "parent" || mode === "kid" ? mode : null;
}

// The active kid's age band, stamped onto kid-mode events that don't set their own —
// what lets deep components (the four Learn & Earn tests, the Arcade) stay free of
// child props they'd only need for analytics. App.jsx keeps this current.
export function setMetricsBand(band) {
  currentBand = typeof band === "string" && band ? band : null;
}

// Exit-stage support (Alan, 26 Aug): the screen_view effects (and the wizard's step
// effect, and the landing page) keep this current; when the app is hidden, app_exit
// reports it. "Exit" is analytics-approximate by nature — a background counts — so the
// dashboard reads the LAST app_exit per session as the session's exit stage.
export function setMetricsScreen(screen) {
  currentScreen = typeof screen === "string" && screen ? screen : null;
}

export function setMetricsHousehold(code) {
  householdCode = typeof code === "string" && code ? code : null;
}

// "Visit" heuristic: a session_start fires when the app becomes active with no
// tracked activity in the previous 30 minutes. sessionStorage means a brand-new tab
// is automatically a new session; the timestamp check covers the PWA-resume case
// where the same tab sleeps for hours.
function maybeSessionStart() {
  safe(() => {
    const last = parseInt(sessionStorage.getItem(LS_LAST_ACTIVE) || "0", 10) || 0;
    if (now() - last > SESSION_GAP_MS) {
      track("session_start", {});
    }
    sessionStorage.setItem(LS_LAST_ACTIVE, String(now()));
  });
}

// Called once from Root when the app shell mounts (before the household loads, so
// pre-signup visitors count too). Idempotent.
export function startMetrics(code) {
  safe(() => {
    setMetricsHousehold(code || null);
    if (started) return;
    started = true;
    maybeSessionStart();
    track("app_open", {});
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        if (currentScreen) track("app_exit", { screen: currentScreen });
        flush();
      } else maybeSessionStart();
    });
    window.addEventListener("pagehide", flush);
  });
}

// Client-side crash visibility (docs/MONITORING_SCOPE.md §4). Uncaught exceptions and
// unhandled promise rejections become app_error events — the one failure class no
// server-side health check can see. Installed once from main.jsx, right after
// startMetrics, so preview/legal/landing surfaces are covered too (the W1.10 lesson).
//
// Guard rails, because an error handler that itself misbehaves is worse than none:
//   - Dedupe: the same message fires once per page load (a render-loop error would
//     otherwise emit hundreds of identical events).
//   - Cap: at most 5 app_error events per page load, full stop.
//   - Scrub: household codes must NEVER reach analytics unhashed (the worker hashes
//     codes deliberately — see worker/handlers/metrics.js), and error messages can
//     embed them via API URLs (/api/household/CODE/...) or ?code= params. Both
//     patterns are masked before the message leaves the device. track() then
//     truncates to 64 chars like every other string prop.
//   - flush() immediately: an error often precedes a broken/abandoned tab, so waiting
//     out the 5s batch window risks losing exactly the events we most want.
const ERROR_MAX_PER_LOAD = 5;
let errorHookInstalled = false;
const seenErrorMessages = new Set();
let errorsReported = 0;

function scrubErrorMessage(message) {
  return String(message)
    .replace(/\/household\/[A-Za-z0-9]{6}/g, "/household/___")
    .replace(/([?&]code=)[A-Za-z0-9]{6}/g, "$1___");
}

// The same scrubbed messages, kept locally so a user reporting a problem can attach
// what actually went wrong instead of trying to describe it (docs/HELP_SCOPE.md §4).
// Never leaves the device except inside a report the user chose to send.
const recentErrors = [];
const errorSubscribers = new Set();

// Lets the app shell offer "something went wrong — tell us?" at the moment it happens,
// which is the entry point that reaches people who'd otherwise just close the tab.
// Subscribers are called with the scrubbed message; a throwing subscriber must never
// take down the error handler, hence the per-subscriber safe().
export function onAppError(fn) {
  errorSubscribers.add(fn);
  return () => errorSubscribers.delete(fn);
}

export function recentErrorMessages() {
  return recentErrors.slice(-5);
}

function reportAppError(kind, message) {
  safe(() => {
    const msg = scrubErrorMessage(message || "unknown");
    const dedupeKey = `${kind}:${msg}`;
    if (errorsReported >= ERROR_MAX_PER_LOAD || seenErrorMessages.has(dedupeKey)) return;
    seenErrorMessages.add(dedupeKey);
    errorsReported++;
    recentErrors.push(`${kind}: ${msg}`);
    if (recentErrors.length > 5) recentErrors.shift();
    track("app_error", { kind, source: msg, screen: currentScreen || undefined });
    flush();
  });
}

export function installErrorHook() {
  safe(() => {
    if (errorHookInstalled) return;
    errorHookInstalled = true;
    // window "error" without capture sees uncaught exceptions only — resource-load
    // errors (a failed <img>) don't bubble and are deliberately not reported.
    window.addEventListener("error", (e) => {
      reportAppError("error", e && e.message);
    });
    window.addEventListener("unhandledrejection", (e) => {
      const reason = e && e.reason;
      reportAppError("rejection", reason && (reason.message || String(reason)));
    });
  });
}

// BUILD_SHA is defined at build time by esbuild (--define). Falls back to "dev" when
// it isn't — a plain `typeof` guard rather than an import, because esbuild replaces a
// free identifier textually and an undefined one would otherwise throw at module load.
function buildSha() {
  try {
    // eslint-disable-next-line no-undef
    return typeof BUILD_SHA === "string" ? BUILD_SHA : "dev";
  } catch {
    return "dev";
  }
}

// Everything a report should carry that the user shouldn't have to type. Nothing here
// is new collection: mode/screen/band already drive analytics, the error ring is
// already scrubbed, and the rest is read off `navigator` at send time.
//
// What's deliberately NOT here: child names, avatars, balances, goals, device labels.
// A report is a support artefact, not a snapshot of the household — same PII line the
// admin panel draws.
export function reportContext(extra) {
  const out = {
    build: buildSha(),
    screen: currentScreen || "",
    mode: currentMode === "kid" || currentMode === "parent" ? currentMode : "",
    ageBand: currentBand || "",
    errors: recentErrorMessages(),
  };
  return safe(() => {
    out.platform = String(navigator.userAgent || "").slice(0, 200);
    out.standalone = !!(window.matchMedia("(display-mode: standalone)").matches || navigator.standalone);
    out.online = navigator.onLine !== false;
    out.url = String(window.location.href || "").replace(/([?&]code=)[A-Za-z0-9]{6}/g, "$1___");
    return Object.assign(out, extra || {});
  }) || Object.assign(out, extra || {});
}

// First-touch attribution (docs/ANALYTICS_SCOPE.md §3.3). Reads ?utm_source /
// utm_medium / utm_campaign (or ?ref= as a shorthand source) into sessionStorage
// BEFORE any signup, fires a first_touch analytics event once per tab, and leaves the
// captured params for the wizard's create call to copy onto the household record as
// settings.acquisition — product data, exact and permanent, joinable to outcomes.
export function captureFirstTouch() {
  safe(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const source = params.get("utm_source") || ref;
    if (!source) return;
    const acq = {
      source: source.slice(0, 64),
      medium: (params.get("utm_medium") || (ref ? "referral" : null) || "").slice(0, 64) || null,
      campaign: (params.get("utm_campaign") || "").slice(0, 64) || null,
      landedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(LS_ACQ, JSON.stringify(acq));
    if (sessionStorage.getItem(LS_FIRST_TOUCH_SENT) !== "1") {
      sessionStorage.setItem(LS_FIRST_TOUCH_SENT, "1");
      track("first_touch", { source: acq.source, medium: acq.medium || undefined, campaign: acq.campaign || undefined });
    }
  });
}

// Read (without consuming) the captured first-touch params — Root's household-create
// path copies these onto settings.acquisition. Null when this visit had none.
export function getAcquisition() {
  return (
    safe(() => {
      const raw = sessionStorage.getItem(LS_ACQ);
      if (!raw) return null;
      const acq = JSON.parse(raw);
      return acq && acq.source ? acq : null;
    }) || null
  );
}

// Shared Learn & Earn funnel telemetry (docs/ANALYTICS_SCOPE.md §3.1 Funnels).
// All four test components share the intro → testing → finished phase shape, so one
// hook covers the whole funnel: game_start on entering "testing", game_complete on
// reaching "finished", game_abandon if the tab hides or the component unmounts
// mid-test (closing the overlay, switching tabs, backgrounding the app).
// React comes from the repo's vendored global (same `const { ... } = React` shape as
// every component file).
export function useGameTelemetry(game, phase, band) {
  const ref = useRef({ phase: null, live: false });
  useEffect(() => {
    const prev = ref.current.phase;
    ref.current.phase = phase;
    if (phase === "testing" && prev !== "testing") {
      ref.current.live = true;
      track("game_start", { game, band: band || undefined });
    } else if (phase === "finished" && ref.current.live) {
      ref.current.live = false;
      track("game_complete", { game, band: band || undefined });
    } else if (phase === "intro" && prev === "testing" && ref.current.live) {
      // restart() mid-test — counts as an abandon of that attempt
      ref.current.live = false;
      track("game_abandon", { game, band: band || undefined, via: "restart" });
    }
  }, [phase]);
  useEffect(() => {
    function onHide() {
      if (document.visibilityState === "hidden" && ref.current.live) {
        ref.current.live = false;
        track("game_abandon", { game, band: band || undefined, via: "background" });
        flush();
      }
    }
    document.addEventListener("visibilitychange", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      if (ref.current.live) {
        ref.current.live = false;
        track("game_abandon", { game, band: band || undefined, via: "close" });
      }
    };
  }, []);
}
